import React from 'react';

import { widgetStyle } from '../../../styles/constants';
import type { TimeFilterProps, TimeFilterStep } from '../../../types';
import logger from '../../../utils/logger';

/**
 * TimeFilter Component - Time-based filtering controls
 *
 * Provides a horizontal row of time filter buttons (1h, 1d, ∞)
 * for filtering devices by age of alarms, positions, or signals.
 * Uses a fixed position at the bottom of the screen.
 *
 * @example
 * <TimeFilter
 *   position={0}
 *   text="Max Alarm Age"
 *   getValue={() => alarmFilter}
 *   setValue={setAlarmFilter}
 * />
 */
export const TimeFilter: React.FC<TimeFilterProps> = ({
  steps = [
    { text: '1h', value: 3600 },
    { text: '1d', value: 24 * 3600 },
    { text: '∞', value: 0 },
  ],
  width = 32,
  text = 'Filter',
  position = 0,
  getValue = () => 3600,
  setValue = (v: number) => logger.debug('No binding for setValue:', v),
}) => {
  const current = getValue();

  /**
   * Determine button color based on current selection and step value
   */
  const getButtonColor = (step: TimeFilterStep): string => {
    if (current === step.value) {
      return current === 0 ? '#44fc' : '#f44c'; // Blue for infinity, red for selected
    }

    if (current === 0) {
      return '#8884'; // Gray when infinity is selected
    }

    if (step.value === 0) {
      return '#44c4'; // Blue for infinity option
    }

    return current < step.value ? '#f444' : '#ccc4'; // Red if less than, gray if more
  };

  const containerStyle: React.CSSProperties = {
    ...widgetStyle,
    position: 'fixed',
    bottom: '4vmin',
    right: `${position * 33}vw`,
    width: `${width}vw`,
    height: '7vmin',
    zIndex: 2000, // Ensure buttons are clickable above other elements
  };

  const buttonRowStyle: React.CSSProperties = {
    height: '4vmin',
    fontSize: '3vmin',
    display: 'flex',
  };

  const labelStyle: React.CSSProperties = {
    height: '2vmin',
    fontSize: '2vmin',
    textAlign: 'center',
  };

  return (
    <div style={containerStyle}>
      {/* Filter buttons */}
      <div style={buttonRowStyle}>
        {steps.map((step, index) => {
          const buttonStyle: React.CSSProperties = {
            height: 'max(36px, 4vmin)',
            width: `${width / steps.length}vw`,
            color: current === step.value ? '#fff' : '#444',
            borderWidth: '1vmin',
            borderColor: '#ccc8',
            backgroundColor: getButtonColor(step),
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            fontWeight: current === step.value ? 'bold' : 'normal',
            pointerEvents: 'auto', // Ensure button can receive clicks
          };

          return (
            <button
              key={index}
              style={buttonStyle}
              onClick={() => {
                logger.debug(`TimeFilter ${text}: Setting value to ${step.value}`);
                setValue?.(step.value);
              }}
              title={`Filter by ${step.text}`}
            >
              {step.text}
            </button>
          );
        })}
      </div>

      {/* Filter label */}
      <div style={labelStyle}>{text}</div>
    </div>
  );
};

export default TimeFilter;
